const PUSH_DEVICE_ID_KEY = 'djelo_push_device_id'
const FIREBASE_CONFIG_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const

let foregroundListenerReady = false

function isValidVapidKey(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false
  }

  const normalized = value.trim()
  return normalized.length >= 80 && /^[A-Za-z0-9_-]+$/.test(normalized)
}

function getFirebaseConfig() {
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  }
}

export function isFirebasePushConfigured(): boolean {
  return FIREBASE_CONFIG_KEYS.every((key) => {
    const value = import.meta.env[key]
    return typeof value === 'string' && value.trim().length > 0
  })
}

export function isPushNotificationsSupported(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return (
    window.isSecureContext &&
    'Notification' in window &&
    'serviceWorker' in navigator
  )
}

export function getPushSupportError(): string | null {
  if (typeof window === 'undefined') {
    return 'unsupported'
  }

  if (!window.isSecureContext) {
    return 'secure_context'
  }

  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return 'unsupported'
  }

  if (!isFirebasePushConfigured()) {
    return 'config_missing'
  }

  if (!isValidVapidKey(import.meta.env.VITE_FIREBASE_VAPID_KEY)) {
    return 'config_missing'
  }

  return null
}

async function ensureServiceWorkerRegistration() {
  const registration = await navigator.serviceWorker.register('/sw.js')
  await navigator.serviceWorker.ready

  if (registration.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' })
  }

  return registration
}

export async function ensurePushNotificationsReady(): Promise<{
  permission: NotificationPermission
  token?: string
}> {
  const supportError = getPushSupportError()
  if (supportError) {
    throw new Error(supportError)
  }

  const registration = await ensureServiceWorkerRegistration()
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return { permission }
  }

  const [{ initializeApp, getApps }, { getMessaging, getToken }] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ])

  const app = getApps()[0] ?? initializeApp(getFirebaseConfig())
  const messaging = getMessaging(app)
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY
  if (!isValidVapidKey(vapidKey)) {
    throw new Error('vapid_missing')
  }

  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  })

  if (!token) {
    throw new Error('token_missing')
  }

  const response = await fetch('/api/notification-settings/register_fcm_token/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      token,
      platform: 'web',
      device_id: getOrCreatePushDeviceId(),
    }),
  })

  if (!response.ok) {
    throw new Error('token_registration_failed')
  }

  return { permission, token }
}

export function showTestNotification(title: string, body: string) {
  if (typeof window === 'undefined' || !('Notification' in window) || Notification.permission !== 'granted') {
    return
  }

  new Notification(title, {
    body,
    icon: '/assets/djelo-sem-fundo.png',
  })
}

export async function registerForegroundPushListener() {
  if (foregroundListenerReady || !isFirebasePushConfigured() || typeof window === 'undefined') {
    return
  }

  const supportError = getPushSupportError()
  if (supportError && supportError !== 'config_missing') {
    return
  }

  const [{ initializeApp, getApps }, { getMessaging, onMessage }] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ])

  const app = getApps()[0] ?? initializeApp(getFirebaseConfig())
  const messaging = getMessaging(app)

  onMessage(messaging, (payload) => {
    const title = payload.notification?.title || 'Djelo Wallet'
    const body = payload.notification?.body || ''
    showTestNotification(title, body)
  })

  foregroundListenerReady = true
}

export function getOrCreatePushDeviceId(): string {
  if (typeof window === 'undefined') {
    return 'server'
  }

  const existing = window.localStorage.getItem(PUSH_DEVICE_ID_KEY)
  if (existing) {
    return existing
  }

  const generated =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `web-${Date.now()}`

  window.localStorage.setItem(PUSH_DEVICE_ID_KEY, generated)
  return generated
}
